import { useMemo } from 'react';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Icon from 'components/Icon';
import { useAppSelector } from 'store/hooks';
import {
  selectContentLibraryPermissions,
  selectContentLibrarySortBy,
} from 'features/content-library/contentLibrarySlice';
import { selectContentLibraryCore } from 'features/content-library/selectors/contentSelectors';
import { gatherContentLibraryItems } from 'features/content-library/utils/gatherContentLibraryItems';
import FileIcon from 'features/content-library/components/content-explorer/FileIcon';
import { StyledContentExplorerList } from 'features/content-library/components/content-explorer/styled';

export type MoveDestinationPickerProps = {
  folderId: string;
  excludeId: string;
  onEnterFolder: (id: string) => void;
};

const listItemTextSlotProps = {
  primary: { noWrap: true },
} as const;

const MoveDestinationPicker = ({
  folderId,
  excludeId,
  onEnterFolder,
}: MoveDestinationPickerProps) => {
  const { t } = useTranslation('content-library');

  const { folders, files, processingMeta } = useAppSelector(selectContentLibraryCore);
  const sortBy = useAppSelector(selectContentLibrarySortBy);
  const { canPresentLibrary, canManagePrivateWhiteboards } = useAppSelector(
    selectContentLibraryPermissions
  );

  // sharedFiles is deliberately not passed: that is what keeps the Shared pseudo-folder,
  // which is not a real destination, out of the picker.
  const entries = useMemo(
    () =>
      gatherContentLibraryItems({
        folders,
        files,
        processingMeta,
        options: {
          recursive: false,
          targetFolderId: folderId,
          canPresentLibrary,
          canManagePrivateWhiteboards,
          sortBy,
        },
      }).filter(
        // A non-folder entry with no type is an upload still in flight, never a destination.
        (entry) => entry.id !== excludeId && (entry.isFolder || entry.type !== null)
      ),
    [
      folders,
      files,
      processingMeta,
      folderId,
      excludeId,
      canPresentLibrary,
      canManagePrivateWhiteboards,
      sortBy,
    ]
  );

  const currentFolder = folders[folderId];

  return (
    <StyledContentExplorerList disablePadding>
      {folderId !== 'root' && currentFolder && (
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => onEnterFolder(currentFolder.parentId || 'root')}
            aria-label={t('folder_back_btn_aria_label')}
          >
            <ListItemIcon>
              <Icon name="back" />
            </ListItemIcon>
            <ListItemText primary={currentFolder.name} slotProps={listItemTextSlotProps} />
          </ListItemButton>
        </ListItem>
      )}
      {entries.map((entry) =>
        entry.isFolder ? (
          <ListItem key={entry.id} disablePadding>
            <ListItemButton onClick={() => onEnterFolder(entry.id)}>
              <ListItemIcon>
                <FileIcon type={entry.type} />
              </ListItemIcon>
              <ListItemText primary={entry.name} slotProps={listItemTextSlotProps} />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem key={entry.id} disablePadding sx={{ px: 2, py: 2, opacity: 0.4 }}>
            <ListItemIcon>
              <FileIcon type={entry.type} />
            </ListItemIcon>
            <ListItemText primary={entry.name} slotProps={listItemTextSlotProps} />
          </ListItem>
        )
      )}
    </StyledContentExplorerList>
  );
};

export default MoveDestinationPicker;
